<template>
  <div class="page">
    <van-nav-bar title="纠偏周报" left-arrow @click-left="$router.back()">
      <template #right><span class="nav-act" @click="generate">生成</span></template>
    </van-nav-bar>
    <van-skeleton v-if="loading" title :row="12"/>
    <template v-else>
      <section class="hero" v-if="data.status !== 'MISSING'">
        <small>{{ data.weekStart }} 起</small>
        <h1>{{ (data.brief && data.brief.title) || data.title || '本周纠偏周报' }}</h1>
        <em :class="data.status">{{ statusLabel }}</em>
      </section>
      <van-empty v-if="data.status === 'MISSING'" description="本周尚未生成周报">
        <van-button type="danger" size="small" @click="generate">立即生成</van-button>
      </van-empty>
      <main v-else>
        <button v-if="data.status === 'CONVEYED'" class="progress-link" @click="$router.push({name:'SupervisorCorrections'})">
          <van-icon name="todo-list-o"/>
          <span>查看员工执行进展</span>
          <van-icon name="arrow"/>
        </button>
        <section v-if="headline">
          <header>
            <van-icon name="bullhorn-o"/>
            <h2>本周重点</h2></header>
          <p class="plain">{{ headline }}</p>
        </section>
        <section v-if="intel.length">
          <header>
            <van-icon name="newspaper-o"/>
            <h2>本周外部情报</h2></header>
          <article v-for="(item, i) in intel" :key="'intel-' + i">
            <b>{{ item.title || item.sourceName || ('情报' + (i + 1)) }}</b>
            <span>{{ item.summary || item.action || item.insight || '' }}</span>
            <a v-if="item.sourceUrl" class="src" :href="item.sourceUrl" target="_blank" rel="noopener">查看来源</a>
          </article>
        </section>
        <section v-if="focusMembers.length">
          <header>
            <van-icon name="friends-o"/>
            <h2>重点关注人员</h2></header>
          <article v-for="(m, i) in focusMembers" :key="'m-' + i">
            <b>{{ m.employeeName || m.name || ('成员' + (i + 1)) }} <small v-if="m.priority">{{ m.priority }}</small></b>
            <ul>
              <li v-for="(act, j) in m.actionList" :key="j">{{ act }}</li>
            </ul>
          </article>
        </section>
        <section v-if="actions.length">
          <header>
            <van-icon name="bulb-o"/>
            <h2>主管本周动作</h2></header>
          <p v-for="(item, index) in actions" :key="'a-' + index"><i>{{
              index + 1
            }}</i><span>{{ typeof item === 'string' ? item : (item.text || item.action || JSON.stringify(item)) }}</span></p>
        </section>
        <div v-if="data.id && data.status !== 'CONVEYED'" class="brief-actions">
          <van-button v-if="data.status === 'UNREAD'" plain type="danger" @click="ack">标记已读</van-button>
          <van-button type="danger" :loading="conveying" @click="convey">确认并下达员工</van-button>
        </div>
      </main>
    </template>
  </div>
</template>

<script>
/**
 * 主管纠偏周报：外部情报（带来源）+ 成员纠偏动作 + 主管动作。
 */
import coreapi from '@/utils/coreapi';

export default {
  data() {
    return {data: {status: 'MISSING', brief: {}}, loading: false, conveying: false};
  },
  computed: {
    brief() {
      return this.data.brief || {};
    },
    headline() {
      return this.brief.teamBrief || this.brief.headline || this.brief.summary || '';
    },
    focusMembers() {
      const rows = this.brief.memberSuggestions || this.brief.focusMembers || [];
      return rows.map(m => ({
        ...m,
        actionList: Array.isArray(m.actions) ? m.actions.slice(0, 3)
          : (m.focus || m.action ? [m.focus || m.action] : [])
      }));
    },
    actions() {
      return this.brief.managerActions || this.brief.actions || [];
    },
    intel() {
      return (this.brief.intelHighlights || this.brief.intelligence || []).slice(0, 3);
    },
    statusLabel() {
      return {UNREAD: '待查看', READ: '待下达', CONVEYED: '已下达', MISSING: '未生成'}[this.data.status] || this.data.status;
    }
  },
  mounted() {
    this.load();
  },
  activated() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.data = await coreapi.supervisorWeeklyBrief();
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.loading = false;
      }
    },
    async generate() {
      this.loading = true;
      try {
        this.data = await coreapi.generateSupervisorWeeklyBrief();
        this.$toast.success('周报已生成');
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.loading = false;
      }
    },
    async ack() {
      try {
        await coreapi.ackSupervisorWeeklyBrief(this.data.id);
        this.$toast.success('已标记已读');
        this.load();
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async convey() {
      if (!this.focusMembers.length) {
        this.$toast('没有可下达的员工纠偏事项');
        return;
      }
      this.conveying = true;
      try {
        await coreapi.conveySupervisorWeeklyBrief(this.data.id);
        this.$toast.success('已下达给相关员工');
        await this.load();
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.conveying = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
@import "~@/assets/base.less";
@import "./supervisor.less";

.page {
  min-height: 100vh;
  padding-bottom: .5rem;
  background: #f6f7fb;
  color: #333
}

.nav-act {
  color: @color;
  font-size: .28rem;
  padding: 0 .08rem
}

.hero {
  margin: .24rem .28rem;
  padding: .3rem;
  background: linear-gradient(145deg, #D32F2F, #B71C1C);
  border-radius: .22rem;
  color: #fff;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .2)
}

.hero small {
  opacity: .85;
  font-size: .24rem
}

.hero h1 {
  margin: .1rem 0 .16rem;
  font-size: .4rem;
  line-height: 1.3
}

.hero em {
  display: inline-block;
  padding: .04rem .14rem;
  border-radius: .2rem;
  background: rgba(255, 255, 255, .18);
  font-size: .22rem;
  font-style: normal
}

main {
  padding: 0 .28rem
}

main > section {
  margin-bottom: .18rem;
  padding: .3rem;
  background: #fff;
  border-radius: .22rem;
  box-shadow: 0 .06rem .24rem rgba(211, 47, 47, .08)
}

main header {
  display: flex;
  align-items: center;
  padding-bottom: .12rem
}

main header .van-icon {
  width: .58rem;
  height: .58rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: .14rem;
  color: @color;
  border-radius: .14rem;
  background: #fff5f5;
  font-size: .32rem
}

h2 {
  font-size: .34rem
}

.plain {
  padding: .14rem 0 0;
  color: #626577;
  font-size: .28rem;
  line-height: 1.5
}

main p {
  padding: .14rem 0;
  color: #626577;
  font-size: .28rem;
  line-height: 1.5;
  border-top: 1PX solid #f0f0f3;
  display: flex;
  align-items: flex-start;
  gap: .12rem
}

main p i {
  width: .38rem;
  height: .38rem;
  line-height: .38rem;
  flex: 0 0 .38rem;
  text-align: center;
  color: #fff;
  border-radius: 50%;
  background: @color;
  font-size: .22rem;
  font-style: normal
}

main article {
  padding: .18rem 0;
  border-top: 1PX solid #eee
}

main article b, main article span {
  display: block
}

main article b {
  color: #34364a;
  font-size: .3rem
}

main article b small {
  margin-left: .1rem;
  color: #d9891b;
  font-size: .22rem;
  font-weight: 400
}

main article span {
  margin-top: .06rem;
  color: #777b90;
  font-size: .26rem;
  line-height: 1.45
}

main article ul {
  margin: .08rem 0 0;
  padding-left: .36rem;
  color: #626577;
  font-size: .26rem;
  line-height: 1.5
}

.src {
  display: inline-block;
  margin-top: .1rem;
  color: @color;
  font-size: .24rem
}

.ack {
  margin: .1rem 0 .2rem
}

.brief-actions {
  margin: .1rem 0 .2rem;
  display: grid;
  grid-template-columns:1fr 1.5fr;
  gap: .16rem
}

.progress-link {
  width: 100%;
  height: .88rem;
  margin-bottom: .18rem;
  padding: 0 .22rem;
  display: flex;
  align-items: center;
  gap: .14rem;
  color: @color;
  background: #fff;
  border-left: .08rem solid @color;
  font-size: .28rem
}

.progress-link span {
  flex: 1;
  text-align: left
}
</style>



// WEBPACK FOOTER //
// src/views/efficiency/supervisor/WeeklyBrief.vue